//ConnectedClassContextProvider
import { PartialMessage, Timestamp } from '@bufbuild/protobuf';
import {
  deserify,
  getEnvConfig,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import {
  BaseStationDetails,
  UpsertConnectedClassQuestionSessionRequest,
  UpsertConnectedClassQuestionSessionRequestV2,
  UpsertConnectedClassQuestionSessionResponse,
  UpsertConnectedClassQuestionSessionResponseV2,
  UpsertConnectedClassResourceSessionRequest,
  UpsertConnectedClassResourceSessionResponse,
  UpsertConnectedClassSessionRequest,
  UpsertConnectedClassSessionRequestV2,
  UpsertConnectedClassSessionResponse,
  UpsertConnectedClassSessionResponseV2,
} from '@protos/learning_management/lms.connected.apis_pb';
import { SessionStatusEnum } from '@protos/learning_management/lms.db_pb';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAppDispatch, useAppSelector } from '../../reduxStore/reduxHooks';
import { IStudentClickerDataObj } from '../../pages/Assessment/ClickerAssessment/reducer/clickerAssessment.slice';

// const firebaseConfig = {
//   apiKey: 'AIzaSyCN2AygzAn3_kzE3Bpkii2VLd0noAekqGA',
//   authDomain: 'schoolnet-india.firebaseapp.com',
//   projectId: 'schoolnet-india',
//   storageBucket: 'schoolnet-india.appspot.com',
//   messagingSenderId: '90784801557',
//   appId: '1:90784801557:web:912197868aab54bb5bbf1c',
//   measurementId: 'G-K1TX7YWLG8',
// };
const config = getEnvConfig();
const firebaseConfig = config.firebaseConfig;

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Cloud Firestore and get a reference to the service
const db = getFirestore(app);

// Define the shape of your context data
interface IContextData {
  dummy_data: boolean;
  authenticateAnonymously: () => void;
  getBaseStationConnectionStatus: (
    props: IgetBaseStationConnectionStatusProps
  ) => Promise<BaseStationDetails | undefined>;
  upsertConnectedClassSessionApiv2: (
    props: PartialMessage<UpsertConnectedClassSessionRequestV2>
  ) => Promise<UpsertConnectedClassSessionResponseV2 | undefined>;
  upsertConnectedClassSessionApiV3: (
    props: PartialMessage<UpsertConnectedClassSessionRequestV2>
  ) => Promise<UpsertConnectedClassSessionResponseV2 | undefined>;
  upsertConnectedClassQuestionSessionApiv2: (
    props: PartialMessage<UpsertConnectedClassQuestionSessionRequestV2>
  ) => Promise<UpsertConnectedClassQuestionSessionResponseV2 | undefined>;
  unsubscribeFromFirestore: () => void;
  caBaseStationData?: ISessionData;
  unsubscribeQuestionFromFirestore: () => void;
  questionSessionData?: IQuestionSessionData;
  // questionSessionIdPrefix: string;
  // upsertConnectedClassResourceSessionApi: (
  //   props: PartialMessage<UpsertConnectedClassResourceSessionRequest>
  // ) => Promise<UpsertConnectedClassResourceSessionResponse | undefined>;
  // upsertConnectedClassQuestionSessionApi: (
  //   props: PartialMessage<UpsertConnectedClassQuestionSessionRequest>
  // ) => Promise<UpsertConnectedClassQuestionSessionResponse | undefined>;
  resetClickerAssessment: () => void;
}
interface IKeypads {
  [keypadSn: string]: boolean;
}
// Create the context
const clickerAssessmentContext = createContext<IContextData | undefined>(
  undefined
);

const { Provider } = clickerAssessmentContext;

// Create a provider component
interface IProviderProps {
  children: ReactNode;
}

export const ClickerAssessmentContextProvider: React.FC<IProviderProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const [userLoggedIn, setUserLoggedIn] = useState<boolean>(false);
  const {
    ca_base_station_details,
    student_list,
    ca_question_session_details,
    clicker_assessment_session_data,
  } = deserify(useAppSelector((store) => store.clickerAssessment));
  // const {
  //   base_station_details,
  //   students_data,
  //   question_session_details,
  //   connectedClass_session_data,
  // } = deserify(useAppSelector((state) => state.connectedClass));
  const {
    LmsConnectedClassAPIServiceV1WithClientStatusCodeHandler,
    LmsConnectedClassAPIServiceV2WithClientStatusCodeHandler,
    LmsConnectedClassAPIServiceV3WithClientStatusCodeHandler,
  } = useCommonServiceClientContext();
  const [unsubscribeFunction, setUnsubscribeFunction] = useState<
    (() => void) | null
  >(null);
  const [caBaseStationData, setCaBaseStationData] = useState<
    ISessionData | undefined
  >();
  const [questionSessionData, setQuestionSessionData] = useState<
    IQuestionSessionData | undefined
  >();
  const [questionSessionIdPrefix, setQuestionSessionIdPrefix] =
    useState<string>('');

  const [unsubscribeQuestionSesssion, setUnsubscribeQuestionSesssion] =
    useState<(() => void) | null>(null);
  const [keypadsOnlineStatus, setKeypadsOnlineStatus] = useState<IKeypads>({});

  const studentsDataRef = useRef(student_list);
  const keypadsOnlineStatusRef = useRef<IKeypads>(keypadsOnlineStatus);

  useEffect(() => {
    studentsDataRef.current = student_list;
    keypadsOnlineStatusRef.current = keypadsOnlineStatus;
  }, [student_list, keypadsOnlineStatus]);

  const resetClickerAssessment = () => {
    setKeypadsOnlineStatus({});
    setUserLoggedIn(false);
    setUnsubscribeFunction(null);
    setQuestionSessionIdPrefix('');
    setQuestionSessionData(undefined);
    // setCaBaseStationData(undefined);
    unsubscribeQuestionFromFirestore();
    unsubscribeFromFirestore();
  };
  const authenticateAnonymously = async () => {
    const auth = getAuth(app);
    return auth.onAuthStateChanged(function (user) {
      if (user) {
        // User is signed in.
        // stopKeypadPolling();
        setUserLoggedIn(true);
        return user;
      } else {
        // No user is signed in.
        console.log('user NIN - ', user);
        setUserLoggedIn(false);
        return signInAnonymously(getAuth(app));
      }
    });
  };
  const getBaseStationConnectionStatus = async (
    props: IgetBaseStationConnectionStatusProps
  ): Promise<BaseStationDetails | undefined> => {
    try {
      const { teacherId, sectionId, classId } = props;
      const res =
        await LmsConnectedClassAPIServiceV1WithClientStatusCodeHandler.getBaseStationConnectionStatus(
          {
            teacherId: teacherId,
            sectionId: sectionId,
            classId: classId,
          }
        );
      console.log('getBaseStationConnectionStatus res', res);
      if (res.status === 200 && res.baseStationDetails) {
        return res.baseStationDetails;
      } else throw new Error(`getBaseStationConnectionStatus error: ${res}`);
    } catch (err) {
      if (err instanceof Error) {
        console.log(err.message);
      } else console.log(err);
    }
  };

  // ----------------------------------------------------

  const upsertConnectedClassSessionApiv2 = async (
    props: PartialMessage<UpsertConnectedClassSessionRequestV2>
  ): Promise<UpsertConnectedClassSessionResponseV2 | undefined> => {
    try {
      const res =
        await LmsConnectedClassAPIServiceV2WithClientStatusCodeHandler.upsertConnectedClassSessionV2(
          props
        );
      if (res && res.status === 200) {
        return res;
      } else throw new Error(`upsertConnectedClassSession error: ${res}`);
    } catch (err) {
      console.log(err);
    }
  };

  // ----------------------------------------------------

  const upsertConnectedClassSessionApiV3 = async (
    props: PartialMessage<UpsertConnectedClassSessionRequestV2>
  ): Promise<UpsertConnectedClassSessionResponseV2 | undefined> => {
    try {
      const res =
        await LmsConnectedClassAPIServiceV3WithClientStatusCodeHandler.upsertConnectedClassSessionV3(
          props
        );
      if (res && res.status === 200) {
        return res;
      } else throw new Error(`upsertConnectedClassSession error: ${res}`);
    } catch (err) {
      console.log(err);
    }
  };

  // ----------------------------------------------------

  const upsertConnectedClassQuestionSessionApiv2 = async (
    props: PartialMessage<UpsertConnectedClassQuestionSessionRequestV2>
  ): Promise<UpsertConnectedClassQuestionSessionResponseV2 | undefined> => {
    try {
      const res =
        await LmsConnectedClassAPIServiceV2WithClientStatusCodeHandler.upsertConnectedClassQuestionSessionV2(
          props
        );
      if (res && res.status === 200) {
        return res;
      } else {
        throw new Error(`upsertConnectedClassResourceSession error: ${res}`);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // ----------------------------------------------------

  const updateKeypadOnlineStatus = (keypadSn: string, isOnline: boolean) => {
    console.log('inside updateKeypadOnlineStatus');
    setKeypadsOnlineStatus((prevStatus) => ({
      ...prevStatus,
      [keypadSn]: isOnline,
    }));
  };
  const handleKeypadStatusChange = async (
    keypad: Keypad,
    data: ISessionData,
    keypadsStatus: IKeypads,
    studentsData: IStudentClickerDataObj[] | undefined
  ) => {
    const previousStatus = keypadsStatus[keypad.keypadSn];
    console.log(
      'handleKeypadStatusChange 1.0',
      previousStatus,
      keypadsOnlineStatus,
      data
    );
    if (
      typeof previousStatus === 'undefined' ||
      previousStatus !== keypad.isOnline
    ) {
      console.log(
        'handleKeypadStatusChange 2.0',
        previousStatus,
        keypad.keypadSn,
        studentsData
      );
      if (studentsData) {
        const sessionIdString = data?.sessionId;
        // Find the studentId based on clickerSerialNo
        const student = studentsData.find(
          (e) => e.clickerSerialNo === keypad.keypadSn
        );
        console.log({ student });
        if (sessionIdString && student) {
          console.log('handleKeypadStatusChange 3.0', previousStatus);
        }
      } // Update the keypad status in state
      updateKeypadOnlineStatus(keypad.keypadSn.toString(), keypad.isOnline);
    }
  };
  const baseStationActiveConnection = async (baseStationSN: string) => {
    const unsub = onSnapshot(
      doc(db, 'baseStations', baseStationSN),
      (doc) => {
        const currentStudentsData = studentsDataRef.current;
        const currentKeypadsStatus = keypadsOnlineStatusRef.current;
        const source = doc.metadata.hasPendingWrites ? 'Local' : 'Server';
        console.log('Doc id: ', doc.id);
        console.log(source, ' data: ', doc.data());
        const data = doc.data() as ISessionData;
        setCaBaseStationData(data);
        if (data) {
          const prefix = `S${data.sessionId}_Q`;
          setQuestionSessionIdPrefix(prefix);
        }
        //creating individual students sessions based on online status
        if (data.sessionId) {
          data.keyPads.forEach((e) =>
            handleKeypadStatusChange(
              e,
              data,
              currentKeypadsStatus,
              currentStudentsData
            )
          );
        }
      },
      (error) => {
        console.log('streamBaseStationUpdate Error');
      }
    );
    setUnsubscribeFunction(() => unsub);
  };
  const questionConnectionBaseStationActiveConnection = (
    baseStationSN: string,
    streamQuestionId: string
  ) => {
    const questionSessionDocRef = doc(
      db,
      'baseStations',
      baseStationSN,
      'questionSession',
      streamQuestionId
    );
    console.log(
      'baseStationSN',
      baseStationSN,
      'streamQuestionId',
      streamQuestionId,
      questionSessionDocRef
    );
    const unsubscribe = onSnapshot(
      questionSessionDocRef,
      (doc) => {
        const source = doc.metadata.hasPendingWrites ? 'Local' : 'Server';
        console.log('QS data: ', doc.data(), streamQuestionId);
        const data = doc.data() as IQuestionSessionData;
        setQuestionSessionData(data);
      },
      (error) => {
        console.log('streamQuestionBaseStationUpdate Error');
      }
    );
    setUnsubscribeQuestionSesssion(() => unsubscribe);
  };
  const unsubscribeFromFirestore = () => {
    if (unsubscribeFunction) {
      unsubscribeFunction(); // Call the unsubscribe function
      setUnsubscribeFunction(null); // Clear the unsubscribe function
      // setCaBaseStationData(undefined);
    }
  };
  const unsubscribeQuestionFromFirestore = () => {
    if (unsubscribeQuestionSesssion) {
      unsubscribeQuestionSesssion(); // Call the unsubscribe function
      setUnsubscribeQuestionSesssion(null); // Clear the unsubscribe function
      setQuestionSessionData(undefined);
    }
  };
  useEffect(() => {
    if (ca_base_station_details?.isOnline) {
      if (userLoggedIn) {
        baseStationActiveConnection(
          ca_base_station_details.baseReceiverSerialNo
        );
      }
      if (!userLoggedIn) {
        authenticateAnonymously();
      }
    }
    return () => {
      unsubscribeFromFirestore();
    };
  }, [ca_base_station_details?.isOnline, userLoggedIn]);

  useEffect(() => {
    if (
      ca_base_station_details?.isOnline &&
      ca_question_session_details &&
      userLoggedIn
    ) {
      questionConnectionBaseStationActiveConnection(
        ca_base_station_details.baseReceiverSerialNo,
        `S${clicker_assessment_session_data?.connectedClassSessionId.toString()}_Q` +
          ca_question_session_details.question_id
      );
    }
    if (!userLoggedIn) {
      authenticateAnonymously();
    }
    return () => {
      unsubscribeQuestionFromFirestore();
    };
  }, [ca_question_session_details?.question_session_id, userLoggedIn]);

  const contextValue: IContextData = {
    dummy_data: true,
    authenticateAnonymously,
    getBaseStationConnectionStatus,
    upsertConnectedClassSessionApiv2,
    upsertConnectedClassSessionApiV3,
    upsertConnectedClassQuestionSessionApiv2,
    unsubscribeFromFirestore,
    unsubscribeQuestionFromFirestore,
    caBaseStationData,
    questionSessionData,
    resetClickerAssessment,
  };
  return <Provider value={contextValue}>{children}</Provider>;
};

export const useClickerAssessmentContext = () => {
  const context = useContext(clickerAssessmentContext);
  if (!context) {
    throw new Error(
      'clickerAssessmentContext must be used within a MyContextProvider'
    );
  }
  return context;
};

interface IgetBaseStationConnectionStatusProps {
  teacherId: bigint;
  sectionId: number;
  classId: number;
}
interface Keypad {
  modifiedOn: Timestamp | null;
  createdOn: Timestamp;
  keypadSn: string;
  isOnline: boolean;
}

export interface ISessionData {
  modifiedOn: Timestamp | null;
  sessionId: number;
  keyPads: Keypad[];
  createdOn: Timestamp;
  isOnline: boolean;
  activeQuestionId: string;
  sessionStatus: boolean;
  baseId: number;
  questionType: number;
}

export interface IResponses {
  createdOn: Timestamp;
  keypadSn: string;
  modifiedOn: Timestamp;
  response: string;
  timeSpent?: number;
}
export interface IQuestionSessionData {
  createdOn: Timestamp;
  question: string;
  questionId: string;
  responses: IResponses[];
}
